/**
 * MyBoki Service Worker Registration
 * Registriert den Service Worker für PWA-Funktionalität
 */

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
  window.location.hostname === '[::1]' ||
  window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/)
);

/**
 * Registriert den Service Worker
 * @param {Object} config - Konfiguration mit onSuccess und onUpdate Callbacks
 */
export function register(config) {
  if ('serviceWorker' in navigator) {
    // Der Service Worker wird nur in Production oder auf localhost registriert
    const publicUrl = new URL(process.env.PUBLIC_URL || '', window.location.href);

    if (publicUrl.origin !== window.location.origin) {
      // Service Worker funktioniert nicht wenn PUBLIC_URL auf einer anderen Origin liegt
      return;
    }

    window.addEventListener('load', () => {
      const swUrl = `${process.env.PUBLIC_URL || ''}/service-worker.js`;

      if (isLocalhost) {
        // Auf localhost: Prüfe ob Service Worker existiert
        checkValidServiceWorker(swUrl, config);

        navigator.serviceWorker.ready.then(() => {
          console.log(
            '[MyBoki] Diese App wird von einem Service Worker gecached. ' +
            'Mehr Infos: https://cra.link/PWA'
          );
        });
      } else {
        // Nicht localhost: Registriere Service Worker
        registerValidSW(swUrl, config);
      }
    });
  }
}

/**
 * Registriert einen validen Service Worker
 */
function registerValidSW(swUrl, config) {
  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      console.log('[MyBoki] Service Worker registriert:', registration.scope);

      // ───────────────────────────────────────────────────────────────────────
      // WICHTIG: Reagiere auf Messages vom Service Worker
      // ───────────────────────────────────────────────────────────────────────
      // SW_UPDATED: Neuer SW wurde aktiviert, Cache wurde geleert
      // RELOAD_REQUIRED: SW fordert Neuladen an (z.B. nach FORCE_REFRESH)
      navigator.serviceWorker.addEventListener('message', (event) => {
        const { data } = event;

        if (data && data.type === 'SW_UPDATED') {
          console.log('[MyBoki] Service Worker Update:', data.version);
          // Informiere den User oder lade automatisch neu
          if (config && config.onUpdate) {
            config.onUpdate(registration);
          }
        }

        if (data && data.type === 'RELOAD_REQUIRED') {
          console.log('[MyBoki] Neuladen erforderlich - Service Worker Update');
          // Automatisches Neuladen nach kurzer Verzögerung
          setTimeout(() => {
            window.location.reload();
          }, 100);
        }
      });

      registration.onupdatefound = () => {
        const installingWorker = registration.installing;

        if (installingWorker == null) {
          return;
        }

        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              // ─────────────────────────────────────────────────────────────────
              // NEUER SW WURDE INSTALLIERT
              // ─────────────────────────────────────────────────────────────────
              // Dank skipWaiting() im SW wird dieser sofort aktiviert.
              // Die index.html sollte NICHT gecacht sein, also ist ein Refresh sicher.
              console.log(
                '[MyBoki] Neuer Service Worker installiert. Seite wird aktualisiert...'
              );

              // Callback für Update (z.B. für Toast-Notification)
              if (config && config.onUpdate) {
                config.onUpdate(registration);
              }

              // Optional: Automatisches Reload nach Update
              // Das ist sicher, da index.html nicht gecacht wird und
              // der neue SW sofort aktiv ist (skipWaiting + clients.claim)
              // Auskommentiert, da onUpdate callback das handhaben sollte:
              // window.location.reload();

            } else {
              // Erster SW-Install (App neu installiert)
              console.log('[MyBoki] Service Worker erstmalig aktiviert.');

              // Callback für Success
              if (config && config.onSuccess) {
                config.onSuccess(registration);
              }
            }
          }
        };
      };

      // ───────────────────────────────────────────────────────────────────────
      // Prüfe regelmäßig auf Updates (alle 60 Sekunden)
      // ───────────────────────────────────────────────────────────────────────
      // Dies stellt sicher, dass Updates schnell erkannt werden,
      // auch wenn der User die Seite lange offen hat.
      setInterval(() => {
        registration.update().catch((err) => {
          console.log('[MyBoki] SW Update-Check fehlgeschlagen:', err);
        });
      }, 60 * 1000);

    })
    .catch((error) => {
      console.error('[MyBoki] Service Worker Registrierung fehlgeschlagen:', error);
    });
}

/**
 * Prüft ob Service Worker valide ist (für localhost)
 */
function checkValidServiceWorker(swUrl, config) {
  fetch(swUrl, {
    headers: { 'Service-Worker': 'script' },
  })
    .then((response) => {
      const contentType = response.headers.get('content-type');

      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf('javascript') === -1)
      ) {
        // Kein Service Worker gefunden - wahrscheinlich andere App
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => {
            window.location.reload();
          });
        });
      } else {
        // Service Worker gefunden - registrieren
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      console.log('[MyBoki] Keine Internetverbindung. App läuft im Offline-Modus.');
    });
}

/**
 * Deregistriert den Service Worker
 */
export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
        console.log('[MyBoki] Service Worker deregistriert.');
      })
      .catch((error) => {
        console.error('[MyBoki] Service Worker Deregistrierung fehlgeschlagen:', error);
      });
  }
}

/**
 * Sendet eine Nachricht an den Service Worker
 * @param {Object} message - Die Nachricht
 * @returns {Promise} Promise mit der Antwort
 */
export function sendMessageToSW(message) {
  return new Promise((resolve, reject) => {
    if (!navigator.serviceWorker.controller) {
      reject(new Error('Kein Service Worker Controller'));
      return;
    }

    const messageChannel = new MessageChannel();
    messageChannel.port1.onmessage = (event) => {
      resolve(event.data);
    };

    navigator.serviceWorker.controller.postMessage(message, [messageChannel.port2]);
  });
}

/**
 * Leert den Service Worker Cache
 * @returns {Promise} Promise wenn Cache geleert wurde
 */
export function clearCache() {
  return sendMessageToSW({ type: 'CLEAR_CACHE' });
}

/**
 * Erzwingt ein Update des Service Workers
 */
export function forceUpdate() {
  if (navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({ type: 'SKIP_WAITING' });
  }
}

/**
 * Gibt die aktuelle Service Worker Version zurück
 * @returns {Promise<Object>} Promise mit cacheVersion und runtimeVersion
 */
export function getVersion() {
  return sendMessageToSW({ type: 'GET_VERSION' });
}

/**
 * Erzwingt ein Neuladen aller Clients
 * Nützlich für Admin-Tools oder Debug-Zwecke
 */
export function forceRefreshAllClients() {
  if (navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({ type: 'FORCE_REFRESH' });
  }
}
